/**
 * WMS Inbound Enhancement Types
 * Shared between backend-wms, web-wms, and mobile-app
 *
 * Includes types for:
 * - Dock Scheduling & Management
 * - Cross-Docking Workflow
 * - Returns Processing (Reverse Logistics)
 * - Advanced Quality Management
 *
 * DRY Compliance: Single source for all inbound enhancement enums
 * Rule 15 Compliance: Proper type definitions
 */

// ============================================================================
// DOCK SCHEDULING ENUMS
// ============================================================================

/**
 * Status of a dock door
 */
export enum DockDoorStatus {
  AVAILABLE = 'AVAILABLE',
  OCCUPIED = 'OCCUPIED',
  MAINTENANCE = 'MAINTENANCE',
  RESERVED = 'RESERVED',
}

/**
 * Type of dock door
 */
export enum DockDoorType {
  STANDARD = 'STANDARD',
  COLD_STORAGE = 'COLD_STORAGE',
  HAZMAT = 'HAZMAT',
  CROSS_DOCK = 'CROSS_DOCK',
  HIGH_BAY = 'HIGH_BAY',
  DRIVE_IN = 'DRIVE_IN',
}

/**
 * Status of a dock appointment
 */
export enum DockAppointmentStatus {
  SCHEDULED = 'SCHEDULED',
  CHECKED_IN = 'CHECKED_IN',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  NO_SHOW = 'NO_SHOW',
  DELAYED = 'DELAYED',
}

/**
 * Priority level for dock appointments
 */
export enum DockAppointmentPriority {
  LOW = 'LOW',
  NORMAL = 'NORMAL',
  HIGH = 'HIGH',
  URGENT = 'URGENT',
}

// ============================================================================
// CROSS-DOCKING ENUMS
// ============================================================================

/**
 * Cross-dock decision types
 */
export enum CrossDockDecision {
  CROSS_DOCK = 'CROSS_DOCK',
  PUTAWAY = 'PUTAWAY',
  STAGING = 'STAGING',
  QUALITY_HOLD = 'QUALITY_HOLD',
}

/**
 * Cross-dock task types
 */
export enum CrossDockTaskType {
  UNLOAD = 'UNLOAD',
  SORT = 'SORT',
  TRANSFER = 'TRANSFER',
  STAGE = 'STAGE',
  LOAD = 'LOAD',
  QUALITY_CHECK = 'QUALITY_CHECK',
  DOCUMENTATION = 'DOCUMENTATION',
}

/**
 * Cross-dock task status
 */
export enum CrossDockTaskStatus {
  PENDING = 'PENDING',
  ASSIGNED = 'ASSIGNED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD',
}

/**
 * Cross-dock status
 */
export enum CrossDockStatus {
  PENDING = 'PENDING',
  MATCHED = 'MATCHED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Cross-dock reason codes
 */
export enum CrossDockReason {
  OUTBOUND_MATCH = 'OUTBOUND_MATCH',
  HIGH_VELOCITY = 'HIGH_VELOCITY',
  BULK_REPLENISHMENT = 'BULK_REPLENISHMENT',
  SEASONAL_DEMAND = 'SEASONAL_DEMAND',
  SPECIAL_ORDER = 'SPECIAL_ORDER',
  QUALITY_ISSUE = 'QUALITY_ISSUE',
  NEW_VENDOR_QC = 'NEW_VENDOR_QC',
}

// ============================================================================
// RETURNS PROCESSING ENUMS
// ============================================================================

/**
 * RMA status
 */
export enum RMAStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  RECEIVED = 'RECEIVED',
  INSPECTING = 'INSPECTING',
  PROCESSED = 'PROCESSED',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
}

/**
 * Return reason codes
 */
export enum ReturnReason {
  DEFECTIVE = 'DEFECTIVE',
  WRONG_ITEM = 'WRONG_ITEM',
  NO_LONGER_NEEDED = 'NO_LONGER_NEEDED',
  DAMAGED = 'DAMAGED',
  QUALITY_ISSUE = 'QUALITY_ISSUE',
  WARRANTY_CLAIM = 'WARRANTY_CLAIM',
  RECALL = 'RECALL',
  OVERSTOCK = 'OVERSTOCK',
  CUSTOMER_PREFERENCE = 'CUSTOMER_PREFERENCE',
  OTHER = 'OTHER',
}

/**
 * Return disposition options
 */
export enum ReturnDisposition {
  RESTOCK = 'RESTOCK',
  REFURBISH = 'REFURBISH',
  SCRAP = 'SCRAP',
  RETURN_TO_VENDOR = 'RETURN_TO_VENDOR',
  LIQUIDATE = 'LIQUIDATE',
  DONATE = 'DONATE',
  HOLD_FOR_INSPECTION = 'HOLD_FOR_INSPECTION',
}

/**
 * Return condition grades
 */
export enum ReturnConditionGrade {
  A = 'A', // Like new / sellable
  B = 'B', // Minor defects / repackage
  C = 'C', // Major defects / refurbish
  D = 'D', // Scrap / unsalvageable
  F = 'F', // Hazardous / dispose
}

// ============================================================================
// ADVANCED QUALITY MANAGEMENT ENUMS
// ============================================================================

/**
 * QC rule types
 */
export enum QCRuleType {
  BY_SUPPLIER = 'BY_SUPPLIER',
  BY_PRODUCT = 'BY_PRODUCT',
  BY_CATEGORY = 'BY_CATEGORY',
  BY_ORIGIN = 'BY_ORIGIN',
  BY_VALUE = 'BY_VALUE',
  BY_QUANTITY = 'BY_QUANTITY',
  CUSTOM = 'CUSTOM',
  /**
   * Auto-evaluated at receiving (no manual inspector/checkpoints): compares the
   * ASN-declared expected lot/expiry (SKT) against what physically arrived and
   * applies the rule's `on_failure` outcome. Validation params (compare lot,
   * compare expiry, min shelf-life days) live in `custom_conditions`.
   */
  LOT_EXPIRY_VALIDATION = 'LOT_EXPIRY_VALIDATION',
}

/**
 * What a matching QC rule does when it "fails" — generalises the legacy binary
 * `is_mandatory` (true → BLOCK, false → NONE). For manual inspection rules,
 * "fail" = inspection not PASS; for LOT_EXPIRY_VALIDATION, "fail" = lot/SKT
 * mismatch or expired stock. Parametric per tenant/client/product/category.
 */
export enum QcOnFailure {
  /** Advisory only — record/inspect for visibility, never gate receiving. */
  NONE = 'NONE',
  /** Surface a warning to the operator; receiving proceeds. */
  WARN = 'WARN',
  /** Receiving proceeds only after explicit operator confirmation/override. */
  REQUIRE_CONFIRM = 'REQUIRE_CONFIRM',
  /** Hard gate — receiving cannot complete until resolved. */
  BLOCK = 'BLOCK',
}

/**
 * QC sampling methods
 */
export enum QCMethod {
  AQL_NORMAL = 'AQL_NORMAL',
  AQL_TIGHTENED = 'AQL_TIGHTENED',
  AQL_REDUCED = 'AQL_REDUCED',
  HUNDRED_PERCENT = 'HUNDRED_PERCENT',
  SKIP_LOT = 'SKIP_LOT',
  RANDOM_SAMPLE = 'RANDOM_SAMPLE',
  FIXED_PERCENTAGE = 'FIXED_PERCENTAGE',
}

/**
 * QC inspection status
 */
export enum QCInspectionStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD',
}

/**
 * QC inspection result
 */
export enum QCInspectionResult {
  PASS = 'PASS',
  FAIL = 'FAIL',
  CONDITIONAL = 'CONDITIONAL',
  PENDING_REVIEW = 'PENDING_REVIEW',
}

/**
 * QC checkpoint result
 */
export enum QCCheckpointResult {
  PASS = 'PASS',
  FAIL = 'FAIL',
  NA = 'NA',
  PENDING = 'PENDING',
}

// ============================================================================
// LABOR MANAGEMENT ENUMS
// ============================================================================

/**
 * Labor task types for receiving
 */
export enum ReceivingLaborTask {
  UNLOADING = 'UNLOADING',
  CHECKING_IN = 'CHECKING_IN',
  QUALITY_CHECK = 'QUALITY_CHECK',
  PUTAWAY = 'PUTAWAY',
  STAGING = 'STAGING',
  DOCUMENTATION = 'DOCUMENTATION',
}

/**
 * Labor performance rating
 */
export enum LaborPerformanceRating {
  BELOW_STANDARD = 'BELOW_STANDARD',
  MEETS_STANDARD = 'MEETS_STANDARD',
  EXCEEDS_STANDARD = 'EXCEEDS_STANDARD',
  OUTSTANDING = 'OUTSTANDING',
}

// ============================================================================
// VENDOR COMPLIANCE ENUMS
// ============================================================================

/**
 * Vendor compliance score categories
 */
export enum VendorComplianceCategory {
  ON_TIME_DELIVERY = 'ON_TIME_DELIVERY',
  ASN_ACCURACY = 'ASN_ACCURACY',
  QUALITY_SCORE = 'QUALITY_SCORE',
  DOCUMENTATION = 'DOCUMENTATION',
  PACKAGING = 'PACKAGING',
  LABELING = 'LABELING',
}

/**
 * Vendor compliance level
 */
export enum VendorComplianceLevel {
  EXCELLENT = 'EXCELLENT',
  GOOD = 'GOOD',
  ACCEPTABLE = 'ACCEPTABLE',
  POOR = 'POOR',
  CRITICAL = 'CRITICAL',
}

// ============================================================================
// UI LABELS & COLORS
// ============================================================================

/**
 * Dock door status labels
 */
export const DockDoorStatusLabels: Record<DockDoorStatus, string> = {
  [DockDoorStatus.AVAILABLE]: 'Available',
  [DockDoorStatus.OCCUPIED]: 'Occupied',
  [DockDoorStatus.MAINTENANCE]: 'Maintenance',
  [DockDoorStatus.RESERVED]: 'Reserved',
};

export const DockDoorStatusColors: Record<DockDoorStatus, string> = {
  [DockDoorStatus.AVAILABLE]: 'green',
  [DockDoorStatus.OCCUPIED]: 'blue',
  [DockDoorStatus.MAINTENANCE]: 'orange',
  [DockDoorStatus.RESERVED]: 'purple',
};

/**
 * Dock appointment status labels
 */
export const DockAppointmentStatusLabels: Record<DockAppointmentStatus, string> = {
  [DockAppointmentStatus.SCHEDULED]: 'Scheduled',
  [DockAppointmentStatus.CHECKED_IN]: 'Checked In',
  [DockAppointmentStatus.IN_PROGRESS]: 'In Progress',
  [DockAppointmentStatus.COMPLETED]: 'Completed',
  [DockAppointmentStatus.CANCELLED]: 'Cancelled',
  [DockAppointmentStatus.NO_SHOW]: 'No Show',
  [DockAppointmentStatus.DELAYED]: 'Delayed',
};

export const DockAppointmentStatusColors: Record<DockAppointmentStatus, string> = {
  [DockAppointmentStatus.SCHEDULED]: 'gray',
  [DockAppointmentStatus.CHECKED_IN]: 'blue',
  [DockAppointmentStatus.IN_PROGRESS]: 'cyan',
  [DockAppointmentStatus.COMPLETED]: 'green',
  [DockAppointmentStatus.CANCELLED]: 'red',
  [DockAppointmentStatus.NO_SHOW]: 'orange',
  [DockAppointmentStatus.DELAYED]: 'yellow',
};

/**
 * Cross-dock decision labels
 */
export const CrossDockDecisionLabels: Record<CrossDockDecision, string> = {
  [CrossDockDecision.CROSS_DOCK]: 'Cross-Dock',
  [CrossDockDecision.PUTAWAY]: 'Putaway',
  [CrossDockDecision.STAGING]: 'Staging',
  [CrossDockDecision.QUALITY_HOLD]: 'Quality Hold',
};

export const CrossDockDecisionColors: Record<CrossDockDecision, string> = {
  [CrossDockDecision.CROSS_DOCK]: 'purple',
  [CrossDockDecision.PUTAWAY]: 'green',
  [CrossDockDecision.STAGING]: 'blue',
  [CrossDockDecision.QUALITY_HOLD]: 'orange',
};

/**
 * Cross-dock status labels
 */
export const CrossDockStatusLabels: Record<CrossDockStatus, string> = {
  [CrossDockStatus.PENDING]: 'Pending',
  [CrossDockStatus.MATCHED]: 'Matched',
  [CrossDockStatus.IN_PROGRESS]: 'In Progress',
  [CrossDockStatus.COMPLETED]: 'Completed',
  [CrossDockStatus.CANCELLED]: 'Cancelled',
};

export const CrossDockStatusColors: Record<CrossDockStatus, string> = {
  [CrossDockStatus.PENDING]: 'gray',
  [CrossDockStatus.MATCHED]: 'blue',
  [CrossDockStatus.IN_PROGRESS]: 'cyan',
  [CrossDockStatus.COMPLETED]: 'green',
  [CrossDockStatus.CANCELLED]: 'red',
};

/**
 * Cross-dock task status labels
 */
export const CrossDockTaskStatusLabels: Record<CrossDockTaskStatus, string> = {
  [CrossDockTaskStatus.PENDING]: 'Pending',
  [CrossDockTaskStatus.ASSIGNED]: 'Assigned',
  [CrossDockTaskStatus.IN_PROGRESS]: 'In Progress',
  [CrossDockTaskStatus.COMPLETED]: 'Completed',
  [CrossDockTaskStatus.CANCELLED]: 'Cancelled',
  [CrossDockTaskStatus.ON_HOLD]: 'On Hold',
};

export const CrossDockTaskStatusColors: Record<CrossDockTaskStatus, string> = {
  [CrossDockTaskStatus.PENDING]: 'gray',
  [CrossDockTaskStatus.ASSIGNED]: 'blue',
  [CrossDockTaskStatus.IN_PROGRESS]: 'cyan',
  [CrossDockTaskStatus.COMPLETED]: 'green',
  [CrossDockTaskStatus.CANCELLED]: 'red',
  [CrossDockTaskStatus.ON_HOLD]: 'orange',
};

/**
 * RMA status labels
 */
export const RMAStatusLabels: Record<RMAStatus, string> = {
  [RMAStatus.PENDING]: 'Pending',
  [RMAStatus.APPROVED]: 'Approved',
  [RMAStatus.RECEIVED]: 'Received',
  [RMAStatus.INSPECTING]: 'Inspecting',
  [RMAStatus.PROCESSED]: 'Processed',
  [RMAStatus.COMPLETED]: 'Completed',
  [RMAStatus.CANCELLED]: 'Cancelled',
  [RMAStatus.REJECTED]: 'Rejected',
};

export const RMAStatusColors: Record<RMAStatus, string> = {
  PENDING: 'gray',
  APPROVED: 'blue',
  RECEIVED: 'cyan',
  INSPECTING: 'yellow',
  PROCESSED: 'purple',
  COMPLETED: 'green',
  CANCELLED: 'red',
  REJECTED: 'red',
};

/**
 * Return disposition labels
 */
export const ReturnDispositionLabels: Record<ReturnDisposition, string> = {
  [ReturnDisposition.RESTOCK]: 'Restock',
  [ReturnDisposition.REFURBISH]: 'Refurbish',
  [ReturnDisposition.SCRAP]: 'Scrap',
  [ReturnDisposition.RETURN_TO_VENDOR]: 'Return to Vendor',
  [ReturnDisposition.LIQUIDATE]: 'Liquidate',
  [ReturnDisposition.DONATE]: 'Donate',
  [ReturnDisposition.HOLD_FOR_INSPECTION]: 'Hold for Inspection',
};

export const ReturnDispositionColors: Record<ReturnDisposition, string> = {
  [ReturnDisposition.RESTOCK]: 'green',
  [ReturnDisposition.REFURBISH]: 'blue',
  [ReturnDisposition.SCRAP]: 'red',
  [ReturnDisposition.RETURN_TO_VENDOR]: 'purple',
  [ReturnDisposition.LIQUIDATE]: 'orange',
  [ReturnDisposition.DONATE]: 'cyan',
  [ReturnDisposition.HOLD_FOR_INSPECTION]: 'yellow',
};

/**
 * Return condition grade labels
 */
export const ReturnConditionGradeLabels: Record<ReturnConditionGrade, string> = {
  [ReturnConditionGrade.A]: 'A - Like New',
  [ReturnConditionGrade.B]: 'B - Minor Defects',
  [ReturnConditionGrade.C]: 'C - Major Defects',
  [ReturnConditionGrade.D]: 'D - Scrap',
  [ReturnConditionGrade.F]: 'F - Hazardous',
};

export const ReturnConditionGradeColors: Record<ReturnConditionGrade, string> = {
  [ReturnConditionGrade.A]: 'green',
  [ReturnConditionGrade.B]: 'yellow',
  [ReturnConditionGrade.C]: 'orange',
  [ReturnConditionGrade.D]: 'red',
  [ReturnConditionGrade.F]: 'red',
};

/**
 * QC method labels
 */
export const QCMethodLabels: Record<QCMethod, string> = {
  [QCMethod.AQL_NORMAL]: 'AQL Normal',
  [QCMethod.AQL_TIGHTENED]: 'AQL Tightened',
  [QCMethod.AQL_REDUCED]: 'AQL Reduced',
  [QCMethod.HUNDRED_PERCENT]: '100% Inspection',
  [QCMethod.SKIP_LOT]: 'Skip Lot',
  [QCMethod.RANDOM_SAMPLE]: 'Random Sample',
  [QCMethod.FIXED_PERCENTAGE]: 'Fixed Percentage',
};

// ============================================================================
// CONSTANTS
// ============================================================================

export const DOCK_DOOR_STATUS_VALUES = [
  'AVAILABLE',
  'OCCUPIED',
  'MAINTENANCE',
  'RESERVED',
] as const;

export const DOCK_APPOINTMENT_STATUS_VALUES = [
  'SCHEDULED',
  'CHECKED_IN',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
  'DELAYED',
] as const;

export const CROSS_DOCK_DECISION_VALUES = [
  'CROSS_DOCK',
  'PUTAWAY',
  'STAGING',
  'QUALITY_HOLD',
] as const;

export const RMA_STATUS_VALUES = [
  'PENDING',
  'APPROVED',
  'RECEIVED',
  'INSPECTING',
  'PROCESSED',
  'COMPLETED',
  'CANCELLED',
  'REJECTED',
] as const;

export const RETURN_DISPOSITION_VALUES = [
  'RESTOCK',
  'REFURBISH',
  'SCRAP',
  'RETURN_TO_VENDOR',
  'LIQUIDATE',
  'DONATE',
  'HOLD_FOR_INSPECTION',
] as const;

export const QC_METHOD_VALUES = [
  'AQL_NORMAL',
  'AQL_TIGHTENED',
  'AQL_REDUCED',
  'HUNDRED_PERCENT',
  'SKIP_LOT',
  'RANDOM_SAMPLE',
  'FIXED_PERCENTAGE',
] as const;

/**
 * Default time slot duration in minutes
 */
export const DEFAULT_TIME_SLOT_DURATION = 60;

/**
 * Time slot preset options for appointment scheduling
 * Used in TimeSlotPresetSelect component
 */
export const TIME_SLOT_PRESETS = [
  { label: '30 minutes', value: 30 },
  { label: '1 hour', value: 60 },
  { label: '2 hours', value: 120 },
  { label: '4 hours', value: 240 },
  { label: 'Full Day (8 hours)', value: 480 },
] as const;

/**
 * Maximum appointments per day per dock door
 */
export const MAX_APPOINTMENTS_PER_DAY = 24;

/**
 * AQL (Acceptable Quality Level) standard values
 */
export const AQL_LEVELS = {
  GENERAL_1: 1.0,
  GENERAL_2: 2.5,
  GENERAL_3: 4.0,
  SPECIAL_1: 0.65,
  SPECIAL_2: 1.0,
  SPECIAL_3: 1.5,
  SPECIAL_4: 2.5,
} as const;

/**
 * Vendor compliance score thresholds
 */
export const VENDOR_COMPLIANCE_THRESHOLDS = {
  EXCELLENT: 95,
  GOOD: 85,
  ACCEPTABLE: 70,
  POOR: 50,
  CRITICAL: 0,
} as const;
